import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  discoverSso,
  signInUrl,
  type AuthProviderId,
  type SsoDiscovery,
} from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Sign-in screen. Renders one button per configured OAuth provider from
 * `/api/auth/providers`. Redirect target is taken from `?redirect=` (safe-
 * listed server-side too) so deep links round-trip through auth cleanly.
 *
 * Behaviour:
 *   - If the user is already signed in, bounce them to `/` immediately.
 *   - If the API has no auth configured (disabled), render an explainer
 *     rather than an empty button row.
 *   - Providers are fetched by AuthContext — we just read them here.
 */
export function SignInPage(): JSX.Element {
  const { user, providers, mode, ready, disabled } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // `?redirect=/crawls/new` etc. Only origin-relative paths are accepted by
  // the server-side `safeRedirect`, but the URL stays in the query string
  // for the full OAuth round trip.
  const redirect = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const raw = sp.get("redirect") ?? "/";
    return raw.startsWith("/") && !raw.startsWith("//") ? raw : "/";
  }, [location.search]);

  // Already signed in — send them on their way.
  if (ready && user) {
    // Use a microtask so the render commits before navigating; avoids the
    // "cannot update during render" warning from React-Router.
    queueMicrotask(() => navigate(redirect, { replace: true }));
    return <div className="text-sm text-slate-400">Redirecting…</div>;
  }

  return (
    <div className="mx-auto max-w-md py-12">
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold">Sign in to UAIP</h1>
          <p className="mt-1 text-sm text-slate-400">
            UAIP is free for personal projects. Sign in to save crawls, raise
            limits, and unlock the live HUD.
          </p>
        </div>

        {disabled ? (
          <DisabledNotice />
        ) : !ready ? (
          <div className="text-sm text-slate-500">Loading sign-in options…</div>
        ) : providers.length === 0 ? (
          <NoProvidersNotice mode={mode} />
        ) : (
          <div className="space-y-4">
            {/* SSO discovery — Plan §21 Phase 5. Type your work email; if
                its domain has SSO configured, a "Continue with SSO"
                button appears above the OAuth buttons. */}
            <SsoDiscover />
            <div className="space-y-3">
              {providers.map((p) => (
                <ProviderButton
                  key={p.id}
                  id={p.id}
                  label={p.label}
                  redirect={redirect}
                />
              ))}
            </div>
          </div>
        )}

        <hr className="my-6 border-slate-800" />

        <p className="text-xs text-slate-500">
          No password, no magic links — we use OAuth to keep credentials out
          of our database. By continuing you agree to the{" "}
          <a className="text-slate-300 hover:text-white" href="/terms">
            terms
          </a>{" "}
          and{" "}
          <a className="text-slate-300 hover:text-white" href="/privacy">
            privacy policy
          </a>
          .
        </p>
      </div>

      <p className="mt-4 text-center text-xs text-slate-500">
        Just browsing?{" "}
        <Link to="/" className="text-slate-300 hover:text-white">
          Back to the dashboard
        </Link>
        .
      </p>
    </div>
  );
}

function ProviderButton(props: {
  id: AuthProviderId;
  label: string;
  redirect: string;
}): JSX.Element {
  const href = signInUrl(props.id, props.redirect);
  return (
    <a
      href={href}
      className="flex w-full items-center justify-center gap-3 rounded-md border border-slate-700 bg-slate-950 px-4 py-3 text-sm font-medium text-slate-100 hover:border-slate-500 hover:bg-slate-900"
    >
      <ProviderIcon id={props.id} />
      <span>Continue with {props.label}</span>
    </a>
  );
}

function ProviderIcon({ id }: { id: AuthProviderId }): JSX.Element {
  if (id === "google") {
    return (
      <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
        <path
          fill="#FFC107"
          d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.2 6.5 29.3 4.5 24 4.5 13 4.5 4 13.5 4 24.5s9 20 20 20 20-9 20-20c0-1.3-.1-2.7-.4-4z"
        />
        <path
          fill="#FF3D00"
          d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.2 6.5 29.3 4.5 24 4.5 16.2 4.5 9.5 8.9 6.3 14.7z"
        />
        <path
          fill="#4CAF50"
          d="M24 44.5c5.2 0 10-1.8 13.7-4.9l-6.3-5.3c-2.1 1.4-4.6 2.2-7.4 2.2-5.4 0-9.7-3.5-11.3-8l-6.5 5C9.4 40 16.2 44.5 24 44.5z"
        />
        <path
          fill="#1976D2"
          d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.8l6.3 5.3c3.3-3 5.7-7.8 5.7-13.6 0-1.3-.1-2.7-.4-4z"
        />
      </svg>
    );
  }
  // GitHub
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 .5A11.5 11.5 0 0 0 .5 12a11.5 11.5 0 0 0 7.86 10.92c.58.11.79-.25.79-.56v-2c-3.2.7-3.88-1.37-3.88-1.37-.52-1.33-1.28-1.68-1.28-1.68-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.2 1.77 1.2 1.03 1.76 2.7 1.25 3.36.95.1-.75.4-1.26.73-1.55-2.55-.3-5.23-1.28-5.23-5.7 0-1.26.45-2.29 1.2-3.1-.12-.3-.52-1.5.11-3.14 0 0 .97-.31 3.19 1.18a11.1 11.1 0 0 1 5.8 0c2.22-1.49 3.19-1.18 3.19-1.18.63 1.64.23 2.84.11 3.14.75.81 1.2 1.84 1.2 3.1 0 4.44-2.69 5.4-5.25 5.69.41.35.77 1.04.77 2.1v3.12c0 .32.21.68.8.56A11.5 11.5 0 0 0 23.5 12 11.5 11.5 0 0 0 12 .5z" />
    </svg>
  );
}

function NoProvidersNotice({ mode }: { mode: string }): JSX.Element {
  return (
    <div className="rounded-md border border-amber-800/70 bg-amber-950/30 px-3 py-3 text-sm text-amber-200">
      <div className="font-medium">No OAuth providers configured.</div>
      <p className="mt-1 text-xs text-amber-200/80">
        The API is running in <code>mode={mode}</code>, but neither Google nor
        GitHub credentials are set. Populate{" "}
        <code className="text-amber-100">GOOGLE_CLIENT_ID</code> /{" "}
        <code className="text-amber-100">GITHUB_CLIENT_ID</code> (and their
        secrets) in <code className="text-amber-100">apps/api/.env</code> and
        restart the API.
      </p>
    </div>
  );
}

function DisabledNotice(): JSX.Element {
  return (
    <div className="rounded-md border border-slate-700 bg-slate-950 px-3 py-3 text-sm text-slate-300">
      <div className="font-medium text-slate-100">Auth isn't enabled here.</div>
      <p className="mt-1 text-xs text-slate-400">
        This deployment of UAIP is running without authentication — every
        visitor has full access. Contact the admin if you expected a sign-in
        screen.
      </p>
    </div>
  );
}

/**
 * SSO domain probe — Plan §21 Phase 5. Type your work email; we
 * debounce-check `/api/sso/discover?email=…` and surface a "Continue
 * with SSO" button if the domain is configured.
 *
 * Empty input: nothing happens. Misspelled domain: silent (no error
 * leak — discovery returns hasSso:false for any unknown domain).
 */
function SsoDiscover(): JSX.Element {
  const [email, setEmail] = useState("");
  const [discovery, setDiscovery] = useState<SsoDiscovery | null>(null);
  const [checking, setChecking] = useState(false);

  // Debounce the discovery probe so we don't fire on every keystroke.
  useEffect(() => {
    const trimmed = email.trim();
    if (!trimmed.includes("@") || trimmed.length < 6) {
      setDiscovery(null);
      return;
    }
    setChecking(true);
    const id = setTimeout(() => {
      void discoverSso(trimmed)
        .then((d) => {
          setDiscovery(d);
        })
        .catch(() => setDiscovery(null))
        .finally(() => setChecking(false));
    }, 350);
    return () => clearTimeout(id);
  }, [email]);

  const ssoDomain = discovery?.hasSso ? discovery.domain : null;

  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/60 p-3">
      <label className="block text-xs font-medium text-slate-400">
        Work email (for SSO)
      </label>
      <input
        type="email"
        autoComplete="email"
        spellCheck={false}
        placeholder="you@company.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500"
      />
      {ssoDomain ? (
        <a
          href={`/api/sso/login?domain=${encodeURIComponent(ssoDomain)}`}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
        >
          Continue with SSO ({ssoDomain})
        </a>
      ) : checking ? (
        <p className="mt-2 text-xs text-slate-500">Checking SSO…</p>
      ) : email.includes("@") ? (
        <p className="mt-2 text-xs text-slate-500">
          No SSO configured for this domain — use one of the OAuth options
          below.
        </p>
      ) : (
        <p className="mt-2 text-xs text-slate-500">
          Optional. If your company uses SAML SSO, type your email to see
          the SSO option.
        </p>
      )}
    </div>
  );
}
